import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';
import type { CatalogPage, DatasetPage } from './contract';

/**
 * Keyset pagination, consumed.
 *
 * The API has returned `next_cursor` since the first migration and has always capped a page at 30
 * rows (`datasets.ts:294`). Every list screen called it with a plain `useQuery`, read `data.data`,
 * and dropped the cursor on the floor — so a tenant with 39 datasets saw 30 of them, the nine that
 * fell off were invisible, and the header above the table counted all 39. A table that silently
 * disagrees with the number printed above it is worse than one that says "there are more".
 *
 * `useInfiniteQuery` rather than page numbers because the cursor is a keyset, not an offset: rows
 * arrive and are removed while an operator reads, and an offset would skip or repeat one when they
 * do. Pages accumulate, which is also what keeps the polling above honest — a refetch re-fetches
 * every page that is on screen rather than resetting to the first.
 */
interface Paged {
  data: unknown[];
  next_cursor: string | null;
}

export interface PagedResult<T extends Paged> {
  /** Every row loaded so far, in order. */
  rows: T['data'];
  /** The first page, for the totals and filter options that only it carries. */
  first: T | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
}

export function usePagedQuery<T extends Paged>(options: {
  queryKey: QueryKey;
  /** `cursor` is null for the first page. */
  fetchPage: (cursor: string | null) => Promise<T>;
  refetchInterval?: number | ((rows: T['data']) => number | false);
}): PagedResult<T> {
  const query = useInfiniteQuery({
    queryKey: options.queryKey,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => options.fetchPage(pageParam),
    getNextPageParam: (last: T) => last.next_cursor ?? undefined,
    refetchInterval: q => {
      const interval = options.refetchInterval;
      if (typeof interval !== 'function') return interval ?? false;
      return interval((q.state.data?.pages ?? []).flatMap(p => (p as T).data));
    }
  });

  const pages = (query.data?.pages ?? []) as T[];
  return {
    rows: pages.flatMap(p => p.data),
    first: pages[0],
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage()
  };
}

export type PagedDatasets = PagedResult<DatasetPage>;
export type PagedCatalog = PagedResult<CatalogPage>;
